import { compositions, Composition } from './compositions';

/**
 * Procedural rave music engine using Web Audio API.
 * Generates beats from composition data - kick, hat, clap, bass, lead.
 */
export class MusicEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private currentComposition = 0;
  private stepIndex = 0;
  private nextStepTime = 0;
  private compositionStartTime = 0;
  private readonly compositionDuration = 30; // seconds per composition
  private schedulerTimer: number | null = null;
  private muted = false;
  private started = false;
  private speakerCount = 1;
  private targetVolume = 0.25;

  // Lookahead scheduling (in seconds)
  private readonly scheduleAhead = 0.1;
  private readonly lookAhead = 25; // ms between scheduler calls

  /** Attempt to start music. Returns false if blocked by autoplay policy. */
  start(): boolean {
    if (this.started) return true;

    try {
      this.ctx = new AudioContext();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = this.muted ? 0 : this.targetVolume;
      this.masterGain.connect(this.ctx.destination);

      // Pick a random starting composition
      this.currentComposition = Math.floor(Math.random() * compositions.length);
      this.stepIndex = 0;
      this.nextStepTime = this.ctx.currentTime + 0.1;
      this.compositionStartTime = this.ctx.currentTime;
      this.started = true;

      this.scheduleLoop();
      return this.ctx.state === 'running';
    } catch {
      return false;
    }
  }

  /** Resume audio context after user gesture */
  resume(): void {
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
    if (!this.started) {
      this.start();
    }
  }

  /** Update volume based on number of speakers. More speakers = louder, with diminishing returns. */
  setSpeakerCount(count: number): void {
    this.speakerCount = Math.max(1, count);
    // 1 speaker = 0.25, 2 = 0.38, 3 = 0.47, 5 = 0.56, 10 = 0.65 (log scale, max ~0.7)
    this.targetVolume = Math.min(0.7, 0.25 + 0.18 * Math.log2(this.speakerCount));
    this.applyVolume();
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    this.applyVolume();
    return this.muted;
  }

  isMuted(): boolean {
    return this.muted;
  }

  /** Suspend audio (for easter eggs etc.) */
  suspend(): void {
    if (this.ctx && this.ctx.state === 'running') {
      this.ctx.suspend();
    }
  }

  private applyVolume(): void {
    if (this.masterGain) {
      this.masterGain.gain.value = this.muted ? 0 : this.targetVolume;
    }
  }

  private scheduleLoop = (): void => {
    if (!this.ctx || !this.masterGain) return;

    const comp = compositions[this.currentComposition];
    const stepDuration = 60 / comp.bpm / 4; // 16th note duration

    while (this.nextStepTime < this.ctx.currentTime + this.scheduleAhead) {
      this.scheduleStep(comp, this.stepIndex, this.nextStepTime);
      this.stepIndex++;

      if (this.stepIndex >= comp.steps) {
        this.stepIndex = 0;
        // Switch composition after ~30 seconds, at a pattern boundary
        const elapsed = this.ctx.currentTime - this.compositionStartTime;
        if (elapsed >= this.compositionDuration) {
          this.currentComposition = (this.currentComposition + 1) % compositions.length;
          this.compositionStartTime = this.ctx.currentTime;
        }
      }

      this.nextStepTime += stepDuration;
    }

    this.schedulerTimer = window.setTimeout(this.scheduleLoop, this.lookAhead);
  };

  private scheduleStep(comp: Composition, step: number, time: number): void {
    if (!this.ctx || !this.masterGain) return;

    if (comp.kick[step % comp.kick.length]) {
      this.playKick(time, comp);
    }

    if (comp.hat[step % comp.hat.length]) {
      this.playHat(time, comp);
    }

    if (comp.clap[step % comp.clap.length]) {
      this.playClap(time);
    }

    const bassNote = comp.bass[step % comp.bass.length];
    if (bassNote !== null) {
      this.playBass(time, bassNote, comp);
    }

    const leadNote = comp.lead[step % comp.lead.length];
    if (leadNote !== null) {
      this.playLead(time, leadNote, comp);
    }
  }

  private playKick(time: number, comp: Composition): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(comp.kickPunch ?? 150, time);
    osc.frequency.exponentialRampToValueAtTime(40, time + 0.08);

    gain.gain.setValueAtTime(0.8, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.3);

    osc.connect(gain);
    gain.connect(this.masterGain!);

    osc.start(time);
    osc.stop(time + 0.3);
  }

  private playHat(time: number, comp: Composition): void {
    const ctx = this.ctx!;
    const bufferSize = ctx.sampleRate * 0.05;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    const hpFilter = ctx.createBiquadFilter();
    hpFilter.type = 'highpass';
    hpFilter.frequency.value = comp.hatTone ?? 7000;

    const gain = ctx.createGain();
    const vol = comp.hatVol ?? 0.3;
    gain.gain.setValueAtTime(vol, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.05);

    noise.connect(hpFilter);
    hpFilter.connect(gain);
    gain.connect(this.masterGain!);

    noise.start(time);
    noise.stop(time + 0.05);
  }

  private playClap(time: number): void {
    const ctx = this.ctx!;
    const bufferSize = ctx.sampleRate * 0.15;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    const bpFilter = ctx.createBiquadFilter();
    bpFilter.type = 'bandpass';
    bpFilter.frequency.value = 1200;
    bpFilter.Q.value = 1.5;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.4, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.12);

    noise.connect(bpFilter);
    bpFilter.connect(gain);
    gain.connect(this.masterGain!);

    noise.start(time);
    noise.stop(time + 0.15);
  }

  private playBass(time: number, midiNote: number, comp: Composition): void {
    const ctx = this.ctx!;
    const freq = midiToFreq(midiNote);
    const stepDur = 60 / comp.bpm / 4;
    const dur = stepDur * 0.8;

    const osc = ctx.createOscillator();
    osc.type = comp.bassWaveform;
    osc.frequency.value = freq;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(comp.filterFreq, time);
    filter.Q.value = comp.filterRes;

    // Filter envelope for acid-style squelch
    if (comp.filterEnvAmount) {
      filter.frequency.setValueAtTime(comp.filterFreq + comp.filterEnvAmount, time);
      filter.frequency.exponentialRampToValueAtTime(comp.filterFreq, time + dur * 0.6);
    }

    const gain = ctx.createGain();
    const vol = comp.bassVol ?? 0.35;
    gain.gain.setValueAtTime(vol, time);
    gain.gain.setValueAtTime(vol, time + dur * 0.7);
    gain.gain.exponentialRampToValueAtTime(0.001, time + dur);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain!);

    osc.start(time);
    osc.stop(time + dur + 0.01);
  }

  private playLead(time: number, midiNote: number, comp: Composition): void {
    const ctx = this.ctx!;
    const freq = midiToFreq(midiNote);
    const stepDur = 60 / comp.bpm / 4;
    const dur = stepDur * (comp.leadSustain ?? 0.6);

    const osc1 = ctx.createOscillator();
    osc1.type = comp.leadWaveform;
    osc1.frequency.value = freq;

    const osc2 = ctx.createOscillator();
    osc2.type = comp.leadWaveform;
    osc2.frequency.value = freq * 1.005; // slight detune for thickness

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = comp.leadFilter ?? 3000;

    const gain = ctx.createGain();
    const vol = comp.leadVol ?? 0.12;
    gain.gain.setValueAtTime(0.001, time);
    gain.gain.linearRampToValueAtTime(vol, time + 0.01);
    gain.gain.setValueAtTime(vol, time + dur * 0.6);
    gain.gain.exponentialRampToValueAtTime(0.001, time + dur);

    osc1.connect(filter);
    osc2.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain!);

    osc1.start(time);
    osc2.start(time);
    osc1.stop(time + dur + 0.01);
    osc2.stop(time + dur + 0.01);
  }

  destroy(): void {
    if (this.schedulerTimer !== null) {
      clearTimeout(this.schedulerTimer);
    }
    if (this.ctx) {
      this.ctx.close();
    }
    this.started = false;
  }
}

function midiToFreq(note: number): number {
  return 440 * Math.pow(2, (note - 69) / 12);
}
